import { HostBinding, inject } from '@angular/core';
import { AppVisualContextService } from 'src/app/shared/services/app-visual-context.service';
import { AppAccent } from 'src/app/core/types/app-accent.type';
import { PCA_RASCUNHOS_STORAGE_KEY } from 'src/app/core/models/pca.model';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { AuthService, UsuarioExternoResponse } from 'src/app/shared/services/auth.service';
import { AppLocalStorageService } from 'src/app/shared/services/app-local-storage.service';
import { PopupService } from 'src/app/shared/services/popup.service';
import { PreferenciaNotificacaoService } from 'src/app/shared/services/preferencia-notificacao.service';
import { Usuario } from 'src/app/core/models/usuario.model';
import { SgrResponse } from 'src/app/core/models/response.model';
import { RascunhoItensCompraService } from 'src/app/shared/services/rascunho-itens-compra.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  private readonly visualContext = inject(AppVisualContextService);
  @HostBinding('class') get visualClass(): string {
    return this.accent === undefined ? '' : this.visualContext.classForAccent(this.accent);
  }

  accent: AppAccent = 'solicitacao';

  hidePassword: boolean = true;
  formCredentials: FormGroup;

  usuarioLogado!: Usuario;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private localStorageService: AppLocalStorageService,
    private popupService: PopupService,
    private preferenciaNotificacaoService: PreferenciaNotificacaoService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Limpa o local storage, mantendo configuração da sidenav, rota de redirecionamento e unidades selecionadas, se houver
    this.localStorageService.clear({ keysToPreserve: ['sidenav_state', 'redirect', 'unidadesSelecionadas', PCA_RASCUNHOS_STORAGE_KEY, RascunhoItensCompraService.STORAGE_KEY, RascunhoItensCompraService.LEGACY_STORAGE_KEY] });
    // Inicializa o formulário de login
    this.formCredentials = this.formBuilder.group({
      cpf: ['', [Validators.required, Validators.minLength(11)]],
      senha: ['', [Validators.required]],
    });
    this.verifyUserLoggedIn();
  }

  togglePassword(): void {
    this.hidePassword = !this.hidePassword;
  }

  verifyUserLoggedIn(): void {
    // Verifica se o token de autenticação foi passado na URL
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      if (params['token']) {
        this.localStorageService.setItem('token', params['token']);

        // Recupera o CPF do usuário logado a partir do token
        let cpf: string | undefined;
        try {
          const decodedToken = this.authService.tokenDecoded as { data?: { user?: { CPF?: string } } } | undefined;
          cpf = decodedToken?.data?.user?.CPF;
        } catch {
          this.popupService.showError('Token inválido para acesso como.');
          return;
        }

        if (!cpf) {
          this.popupService.showError('Token inválido para acesso como.');
          return;
        }

        // Recupera o fragmento da URL com a rota de redirecionamento e seus parâmetros
        const urlRedirect = this.activatedRoute.snapshot.fragment ?? undefined;
        this.authService.acessarComo(cpf, urlRedirect).subscribe();
      }
    });
  }

  submit() {
    if (this.formCredentials.invalid) {
      this.popupService.showError('Preencha as informações de login!');
      return;
    }
    const bodyCredentials = JSON.stringify(this.formCredentials.value);

    this.authService
      .login({ body: bodyCredentials, spinnerDelay: 300 })
      .pipe(take(1))
      .subscribe({
        next: (response: SgrResponse) => {
          if (response['error'] || !response['data']?.length) {
            this.tentaLoginExterno(bodyCredentials);
            return;
          }
          const token = response['data'][0].token;
          this.onLoginSuccess(token);
        },
        error: () => {
          this.tentaLoginExterno(bodyCredentials);
        },
      });
  }

  /**
   * Fallback quando as credenciais não pertencem a um funcionário: tenta autenticar como
   * usuário externo (ex.: defensor vinculado a um PAD) antes de reportar falha de login.
   */
  private tentaLoginExterno(bodyCredentials: string): void {
    this.authService
      .loginExterno({ body: bodyCredentials, spinnerDelay: 300 })
      .pipe(take(1))
      .subscribe({
        next: (response: UsuarioExternoResponse) => {
          if (response.error || !response.token) {
            this.popupService.showError('Falha ao realizar login!');
            return;
          }
          this.onLoginExternoSuccess(response.token);
        },
        error: () => {
          this.popupService.showError('Falha ao realizar login!');
        },
      });
  }

  usuarioNameDisplay(): string {
    let nome = this.usuarioLogado.NOME;
    let primeiroNome = nome.split(' ')[0];
    return primeiroNome;
  }

  private onLoginSuccess(token: string): void {
    // Salva o token no local storage
    this.localStorageService.setItem('token', token);
    this.preferenciaNotificacaoService.refresh();
    // Recupera a rota anterior pretendida, se houver.
    const redirectUrl = this.localStorageService.getItem('redirect');
    this.localStorageService.removeItem('redirect');
    this.usuarioLogado = this.authService.usuarioLogado() as Usuario;

    // Faz a navegação removendo “/login” do histórico, impedindo que Voltar derrube a sessão.
    this.router
      .navigateByUrl(redirectUrl || '/unidade/solicitacao', {
        replaceUrl: true,
      })
      .then(() => {
        // Mensagem de boas vindas
        this.popupService.showSuccess(`Seja bem‑vindo ${this.usuarioNameDisplay()}!`);
      });
  }

  /**
   * Sessão de usuário externo (ex.: defensor de PAD) é independente da sessão interna de funcionário —
   * guarda o token sob uma chave própria em vez de 'token', para não colidir com usuarioLogado().
   */
  private onLoginExternoSuccess(token: string): void {
    this.localStorageService.setItem('tokenExterno', token);
    this.router.navigateByUrl('/externo', { replaceUrl: true });
  }

}
